import type { CompoundIngredientDraft, RxDraft } from '@/entities/rx';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/shared/ui/shadcn/field';
import { Input } from '@/shared/ui/shadcn/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/shadcn/select';

import type { RxFormApi } from '../../model/types';
import { getFieldState } from '@/shared/lib/forms';
import { IngredientMedicationSelector } from './IngredientMedicationSelector';

type CompoundDraft = Extract<RxDraft, { kind: 'compound' }>;

interface Props {
  form: RxFormApi;
}

export function IngredientsEditor({ form }: Props) {
  return (
    <FieldSet>
      <FieldLegend variant="label">Ingredients</FieldLegend>
      <FieldDescription>Add at least one ingredient.</FieldDescription>

      <FieldGroup>
        <form.Field
          name="ingredients"
          mode="array"
          children={(ingredientsField) => {
            const ingredients =
              (ingredientsField.state.value as CompoundDraft['ingredients'] | undefined) ?? [];
            const { isInvalid, errors } = getFieldState(ingredientsField.state.meta);

            return (
              <div className="flex flex-col gap-3">
                {isInvalid ? <FieldError errors={errors} /> : null}

                {ingredients.length === 0 && (
                  <div className="text-muted-foreground text-sm">No ingredients yet.</div>
                )}

                {ingredients.map((ing: CompoundIngredientDraft, idx: number) => (
                  <IngredientRow
                    key={`ingredients[${idx}]`}
                    form={form}
                    ingredient={ing}
                    index={idx}
                  />
                ))}

                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    void form.pushFieldValue(
                      'ingredients' as never,
                      {
                        kind: 'coded',
                        DispensableDrugId: 0,
                        Quantity: '',
                      } as never
                    )
                  }
                >
                  Add ingredient
                </Button>
              </div>
            );
          }}
        />
      </FieldGroup>
    </FieldSet>
  );
}

interface RowProps {
  form: RxFormApi;
  ingredient: CompoundIngredientDraft;
  index: number;
}

function IngredientRow({ form, ingredient, index }: RowProps) {
  const base = `ingredients[${index}]`;

  const switchKind = (nextKind: string) => {
    if (nextKind === ingredient.kind) return;
    const next: CompoundIngredientDraft =
      nextKind === 'coded'
        ? {
            kind: 'coded',
            DispensableDrugId: 0,
            Quantity: ingredient.Quantity ?? '',
          }
        : { kind: 'freeText', FreeText: '', Quantity: ingredient.Quantity ?? '' };
    form.replaceFieldValue('ingredients' as never, index, next as never);
  };

  return (
    <div className="rounded-md border p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="text-sm font-medium">Ingredient {index + 1}</div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => void form.removeFieldValue('ingredients' as never, index)}
        >
          Remove
        </Button>
      </div>

      {/* Kind toggle */}
      <div className="mb-3">
        <Select value={ingredient.kind} onValueChange={switchKind}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent position="item-aligned">
            <SelectItem value="coded">Coded</SelectItem>
            <SelectItem value="freeText">Free text</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Coded vs FreeText */}
      {ingredient.kind === 'coded' ? (
        <form.Field
          name={`${base}.DispensableDrugId` as never}
          children={(drugField) => {
            const id = (drugField.state.value as number | null | undefined) ?? null;

            return (
              <IngredientMedicationSelector
                label="Medication"
                value={id}
                errorMeta={drugField.state.meta}
                onChange={({ dispensableDrugId, dispenseUnitId }) => {
                  drugField.handleChange(() => dispensableDrugId as never);
                  if (dispenseUnitId != null) {
                    form.setFieldValue(`${base}.DispenseUnitId` as never, dispenseUnitId as never);
                  }
                }}
              />
            );
          }}
        />
      ) : (
        <form.Field
          name={`${base}.FreeText` as never}
          children={(textField) => {
            const { isInvalid, errors } = getFieldState(textField.state.meta);
            return (
              <Field data-invalid={isInvalid}>
                <FieldLabel htmlFor={textField.name}>Ingredient name</FieldLabel>
                <Input
                  id={textField.name}
                  name={textField.name}
                  value={(textField.state.value as string) ?? ''}
                  onBlur={textField.handleBlur}
                  onChange={(e) => textField.handleChange(e.target.value as never)}
                  placeholder="e.g. Petrolatum base"
                  aria-invalid={isInvalid}
                />
                {isInvalid ? <FieldError errors={errors} /> : null}
              </Field>
            );
          }}
        />
      )}

      {/* Quantity */}
      <form.Field
        name={`${base}.Quantity` as never}
        children={(qtyField) => {
          const { isInvalid, errors } = getFieldState(qtyField.state.meta);
          return (
            <Field data-invalid={isInvalid} className="mt-3">
              <FieldLabel htmlFor={qtyField.name}>Quantity</FieldLabel>
              <Input
                id={qtyField.name}
                name={qtyField.name}
                value={(qtyField.state.value as string) ?? ''}
                onBlur={qtyField.handleBlur}
                onChange={(e) => qtyField.handleChange(e.target.value as never)}
                placeholder="e.g. 10"
                aria-invalid={isInvalid}
              />
              {isInvalid ? <FieldError errors={errors} /> : null}
            </Field>
          );
        }}
      />
    </div>
  );
}
